/**
 * Admin-ops API client — Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C_OPS.
 *
 * Mirrors `auteur_cloud/api/routes/admin_ops.py`'s read and action routes, one
 * function per route. Every function that names a tenant takes an explicit
 * `reason` — the server refuses with `400` if it is missing or blank (rule
 * U-5: every operator action against tenant data is audited with a
 * justification), so this client never lets a call go out without one.
 *
 * Authentication is the operator's own `__Host-auteur_session` cookie —
 * `credentials: 'same-origin'` sends it, exactly as
 * `pages/ProviderKeys/providerKeysApi.js` already does. No separate
 * "admin token" exists or is read from storage here.
 */

import { requestJson } from '../../api/http';

const BASE = '/v1/admin/tenants';

/** Shared fetch wrapper. Never logs a request body. */
async function call(path, { method = 'GET', body, query } = {}) {
  const url = query ? `${path}?${new URLSearchParams(query).toString()}` : path;
  return requestJson(url, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** `GET /v1/admin/tenants` — every tenant's status/slug/display name. No reason required. */
export function listTenants({ status } = {}) {
  return call(BASE, { query: status ? { status } : undefined });
}

/** `POST /v1/admin/tenants` — provision a tenant and its first owner. */
export function provisionTenant({ slug, displayName, ownerPrincipalId, reason }) {
  return call(BASE, {
    method: 'POST',
    body: { slug, display_name: displayName, owner_principal_id: ownerPrincipalId, reason },
  });
}

/** `GET /v1/admin/tenants/{orgId}` — the support view: tenant + projects + usage. */
export function getTenant(orgId, { reason }) {
  return call(`${BASE}/${encodeURIComponent(orgId)}`, { query: { reason } });
}

/** `POST /v1/admin/tenants/{orgId}/suspend`. */
export function suspendTenant(orgId, { reason }) {
  return call(`${BASE}/${encodeURIComponent(orgId)}/suspend`, { method: 'POST', body: { reason } });
}

/** `POST /v1/admin/tenants/{orgId}/reinstate`. */
export function reinstateTenant(orgId, { reason }) {
  return call(`${BASE}/${encodeURIComponent(orgId)}/reinstate`, { method: 'POST', body: { reason } });
}

/**
 * `POST /v1/admin/tenants/{orgId}/close` — close-with-export.
 *
 * The server refuses (`409 export_incomplete`) until every project's
 * one-click export has succeeded; this client surfaces that as an ordinary
 * thrown error (`failure.code === 'export_incomplete'`), not a special case.
 */
export function closeTenant(orgId, { reason }) {
  return call(`${BASE}/${encodeURIComponent(orgId)}/close`, { method: 'POST', body: { reason } });
}

/** `GET /v1/admin/tenants/{orgId}/usage` — storage, jobs, tokens (estimated vs. billed). */
export function getTenantUsage(orgId, { reason }) {
  return call(`${BASE}/${encodeURIComponent(orgId)}/usage`, { query: { reason } });
}

/** `GET /v1/admin/tenants/{orgId}/audit` — this tenant's audit trail, and no other's. */
export function getTenantAudit(orgId, { reason, decision }) {
  const query = decision ? { reason, decision } : { reason };
  return call(`${BASE}/${encodeURIComponent(orgId)}/audit`, { query });
}

/** `GET /v1/admin/notifications/status` — SMTP presence and queue aggregates. */
export function getNotificationStatus() {
  return call('/v1/admin/notifications/status');
}

/**
 * The complete set of admin-ops operations available to this client.
 *
 * Exported as data, mirroring `PROVIDER_KEY_OPERATIONS`, so a future review of
 * what this surface can do does not have to re-derive it from the functions
 * above.
 */
export const ADMIN_OPS_OPERATIONS = Object.freeze([
  'list',
  'provision',
  'get',
  'suspend',
  'reinstate',
  'close',
  'usage',
  'audit',
  'notifications.status',
]);
